import { readBoardingAccommodationTotals, validBoardingDiscount } from "@/utils/boardingDiscount";
import { ref, type Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { Controllers, FrappeMethodType } from "@/type/api";
import type {
  BoardingBillableItemsPayload,
  BoardingBillableItemWrite,
  BoardingCancelPayload,
  BoardingCheckInPayload,
  BoardingCheckInResult,
  BoardingCheckOutPayload,
  BoardingCheckOutResult,
  BoardingDetail,
  BoardingFilterParam,
  BoardingMedicationDispensePayload,
  BoardingMedicationGivenPayload,
  BoardingOrderPayload,
  BoardingOrderResult,
  BoardingPaymentPayload,
  BoardingPaymentResult,
  BoardingRecordFilterParam,
  BoardingRecordListItem,
  BoardingReservationPayload,
  BoardingUnitSummary,
  ServiceRoom,
} from "@/type/healthcare";
import {
  mapBoardingDetail,
  mapBoardingRecord,
  mapBoardingUnit,
  normalizeMethodListResponse,
  normalizeMethodPayload,
  readBoardingPayments,
} from "@/api/healthcareApi.utils";
import { HEALTHCARE_BOARDING_USE_MOCKS } from "@/modules/healthcare/config";
import {
  mockCheckInBoarding,
  mockCheckOutBoarding,
  mockGetBoardingDetail,
  mockGetBoardingRecords,
  mockGetBoardingUnits,
  mockRecordBoardingPayment,
  mockReserveBoardingRoom,
  mockSyncBoardingBillableItems,
} from "@/modules/healthcare/mock";
import { getMockServiceRoomsSnapshot } from "@/api/serviceRoomApi";
import { withFrappeLoading, type FrappeListResult } from "@/services/frappe/list";
import { serializeFrappeFilters } from "@/services/frappe/filters";
import { readErrorsMessage, unwrapStandardEnvelope } from "@/services/frappe/envelope";

/**
 * `errors[].message`, markup stripped — and NOTHING ELSE.
 *
 * `readErrorsMessage` is the one reader for this, shared with the response interceptor so a
 * refusal reads identically wherever it is caught. It never falls back to `errors[0].details`:
 * on these endpoints `details` is a **full Python traceback including server file paths**
 * (contract §6), which is diagnostic output for us and must never reach a desk user. It never
 * falls back to `meta.code` either — "ValidationError" is not an instruction. An empty result
 * is the correct outcome when the backend sent no usable words, and the caller supplies its own.
 */
const boardingErrorMessage = readErrorsMessage;

const boardingService = new HttpClient(Controllers.petBoarding);

/**
 * Every boarding write goes through here.
 *
 * These endpoints report a refusal as **HTTP 200 with `ok: false`** (contract Part C) — the
 * status line says nothing, so a path that does not read `ok` treats a refusal as a success.
 * That is not a cosmetic bug: `check_in_boarding`, `cancel_boarding` and `sync_billable_items`
 * used to return `true`/a result object built from an EMPTY payload, and the desk got a green
 * "Checked in" toast for an animal the backend had refused to admit.
 *
 * The interceptor catches the refusal for the response shapes it can see; this is the assertion
 * at the endpoint itself, so the guarantee does not depend on where the backend happened to put
 * the envelope. The thrown message is the backend's own — it names the record to create or the
 * field to correct, and every boarding caller renders it verbatim.
 */
function assertBoardingOk(response: any, method: string): Record<string, any> {
  const envelope = unwrapStandardEnvelope<Record<string, any>>(response);

  if (envelope && !envelope.ok) {
    const reason = boardingErrorMessage(envelope.errors);

    throw new Error(reason || `${method} was refused`);
  }

  return (envelope?.data ?? normalizeMethodPayload<any>(response) ?? {}) as Record<string, any>;
}

const BOARDING_METHODS = {
  list: "pet_app.api.healthcare.boarding.list_boarding_units",
  get: "pet_app.api.healthcare.boarding.get_boarding_detail",
  records: "pet_app.api.healthcare.boarding.list_boarding_records",
  reserve: "pet_app.api.healthcare.boarding.reserve_room",
  checkIn: "pet_app.api.healthcare.boarding.check_in_boarding",
  checkOut: "pet_app.api.healthcare.boarding.check_out_boarding",
  syncBillableItems: "pet_app.api.healthcare.boarding.sync_billable_items",
  createOrder: "pet_app.api.healthcare.boarding.create_order",
  startVisitBoarding: "pet_app.api.healthcare.boarding.start_visit_boarding",
  cancel: "pet_app.api.healthcare.boarding.cancel_boarding",
  recordMedicationGiven: "pet_app.api.healthcare.boarding.record_medication_given",
  dispenseMedication: "pet_app.api.healthcare.boarding.dispense_medication",
  departOccupant: "pet_app.api.healthcare.boarding.depart_occupant",
  transferOccupantRoom: "pet_app.api.healthcare.boarding.transfer_occupant_room",
  addOccupant: "pet_app.api.healthcare.boarding.add_occupant",
  recordPayment: "pet_app.api.healthcare.boarding.record_boarding_payment",
} as const;

function buildMockBoardingPayload(
  room: ServiceRoom,
  boarding?: any | null,
) {
  return {
    ...(boarding || {}),
    id: room.id,
    boarding_record_id: boarding?.id ?? null,
    room_id: room.id,
    service_room_id: room.id,
    unit_code: room.roomCode,
    unit_label: room.roomName,
    room_name: room.roomName,
    room_type: room.roomType,
    room_image: room.image,
    room_status: room.status,
    status: boarding?.status ?? "Available",
    record_status: boarding?.recordStatus ?? null,
    reserved_at: boarding?.reservedAt ?? null,
    billable_items: boarding?.billableItems ?? [],
  };
}

async function getMockBoardingRooms() {
  const [rooms, boardings] = await Promise.all([
    Promise.resolve(getMockServiceRoomsSnapshot()),
    mockGetBoardingUnits(),
  ]);

  return rooms
    .filter(room => String(room.status || "Active").toLowerCase() !== "inactive")
    .map((room) => {
    const activeBoarding = boardings.find(item =>
      item.roomId === room.id || item.unitCode === room.roomCode,
    );

    return activeBoarding ? buildMockBoardingPayload(room, activeBoarding) : buildMockBoardingPayload(room);
    });
}

export async function GetPetBoardingUnits(
  loading: Ref<boolean>,
  filter: BoardingFilterParam = {},
): Promise<BoardingUnitSummary[]> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS) {
      const mockRooms = await getMockBoardingRooms();
      return mockRooms.map(mapBoardingUnit);
    }

    try {
      const response = await boardingService.get<any>(
        BOARDING_METHODS.list,
        {},
        {
          search: filter.search,
          occupancy: filter.occupancy,
          date: filter.date,
        },
        undefined,
        undefined,
        FrappeMethodType.method,
      );

      return normalizeMethodListResponse<any>(response).data.map(mapBoardingUnit);
    } catch (error: any) {
      console.error(error?.message ?? error);
      return [];
    }
  });
}

export async function GetPetBoardingDetail(
  loading: Ref<boolean>,
  params: {
    boardingId?: string | null;
    roomId?: string | null;
    name?: string | null;
  },
): Promise<BoardingDetail | null> {
  return withFrappeLoading(loading, async () => {
    const boardingId = params.boardingId || "";
    const roomId = params.roomId || "";
    const lookupName = params.name || boardingId || roomId;

    if (HEALTHCARE_BOARDING_USE_MOCKS) {
      const record = boardingId
        ? await mockGetBoardingDetail(boardingId)
        : null;
      if (record)
        return mapBoardingDetail(record);

      const rooms = await getMockBoardingRooms();
      const response = rooms.find(room => room.id === roomId || room.id === lookupName)
        || rooms.find(room => room.room_id === roomId || room.room_id === lookupName)
        || null;

      return response ? mapBoardingDetail(response) : null;
    }

    try {
      const response = await boardingService.get<any>(
        BOARDING_METHODS.get,
        {},
        {
          boarding_id: boardingId || undefined,
          room_id: roomId || undefined,
          name: lookupName || undefined,
        },
        undefined,
        undefined,
        FrappeMethodType.method,
      );

      const payload = normalizeMethodPayload<any>(response);

      const mapped = payload ? mapBoardingDetail(payload) : null;

      return mapped;
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function GetPetBoardingRecords(
  loading: Ref<boolean>,
  filter: BoardingRecordFilterParam = {},
): Promise<FrappeListResult<BoardingRecordListItem> | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS) {
      const response = await mockGetBoardingRecords(filter);

      return {
        data: response.data.map(mapBoardingRecord),
        total: response.total,
      };
    }

    try {
      const serializedFilters = serializeFrappeFilters(filter.filters ?? []);
      const serializedOrFilters = serializeFrappeFilters(filter.orFilters ?? []);

      const response = await boardingService.get<any>(
        BOARDING_METHODS.records,
        {},
        {
          search: filter.search,
          // Legacy explicit params kept for backwards compatibility / mock path.
          status: filter.status,
          pet_id: filter.petId,
          guardian_id: filter.guardianId,
          date_from: filter.dateFrom,
          date_to: filter.dateTo,
          limit_start: filter.limit_start,
          limit_page_length: filter.limit_page_length,
          order_by: filter.order_by,
          ...(serializedFilters ? { filters: serializedFilters } : {}),
          ...(serializedOrFilters ? { or_filters: serializedOrFilters } : {}),
        },
        undefined,
        undefined,
        FrappeMethodType.method,
      );

      const normalized = normalizeMethodListResponse<any>(response);

      return {
        data: normalized.data.map(mapBoardingRecord),
        total: normalized.total,
      };
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function ReservePetBoardingRoom(
  loading: Ref<boolean>,
  payload: BoardingReservationPayload,
): Promise<{ roomId: string; boardingId: string } | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return mockReserveBoardingRoom(payload);

    // Two shapes, ONE endpoint (backend accepts both — contract §reserve_room):
    //  • assign  → { roomId, boarding_id }  (pet/guardian NOT re-sent; they're on the boarding)
    //  • reserve → the original payload, byte-for-byte unchanged (regression-safe)
    // Errors PROPAGATE (no swallow) so the caller surfaces the backend's real message — e.g.
    // "Configure Treatment Boarding Item in Pet Boarding Settings" — instead of a blind toast.
    /**
     * Three shapes, ONE endpoint (contract Part B):
     *  • assign        → { roomId, boarding_id }  (pet params are IGNORED on this path)
     *  • reserve multi → petIds, a real JSON array; the first entry is the nominal pet
     *  • reserve legacy→ petId, unchanged
     *
     * `petId` and `petIds` are never sent TOGETHER: the backend accepts both only when they
     * agree and refuses when they disagree, so sending one is the only shape that cannot be
     * self-contradictory.
     */
    const body = payload.boardingId
      ? { roomId: payload.roomId, boarding_id: payload.boardingId }
      : payload.petIds?.length
        ? { ...payload, petIds: payload.petIds, petId: undefined }
        : payload;

    const response = await boardingService.post<any, typeof body>(
      body,
      BOARDING_METHODS.reserve,
      {},
      undefined,
      FrappeMethodType.method,
    );

    /**
     * Envelope discipline, so a refusal arrives as the backend's own words.
     *
     * This used to infer success from `boarding_id ?? name` and return null otherwise
     * (BOARDING_GAPS #13) — which turned every per-pet refusal into a blind "reserve failed".
     * Those messages are the whole value of the multi-pet path: they name the pet that failed
     * ("Pet X is not linked to Guardian Y", "Pet X is already on boarding BRD-…", "This booking
     * would hold 2 pets. The limit is 1"). Throwing with the message lets the caller show it.
     */
    const normalized = assertBoardingOk(response, BOARDING_METHODS.reserve);
    const roomId = normalized?.room_id ?? payload.roomId;
    const boardingId = normalized?.boarding_id ?? normalized?.name;

    return roomId && boardingId
      ? { roomId: String(roomId), boardingId: String(boardingId) }
      : null;
  });
}

/**
 * The three occupant endpoints share one shape, so they share one caller.
 *
 * ⚠️ They wrap failures in `ok: false` **WITHOUT RAISING** (contract Part C, closing note), so
 * branching on the HTTP status would treat every refusal as a success. `ok` is the only signal.
 * Refusals are thrown with the backend's own words — they are the entire value of these
 * endpoints, naming the pet, the room, the branch, or telling the operator to check out instead.
 *
 * All three return the FULL booking under `boarding`, so callers replace the roster wholesale
 * rather than patching rows by hand (§5).
 */
async function postOccupantAction<TBody extends Record<string, unknown>>(
  method: string,
  body: TBody,
): Promise<{ data: Record<string, any>; boarding: BoardingDetail | null }> {
  const response = await boardingService.post<any, TBody>(
    body,
    method,
    {},
    undefined,
    FrappeMethodType.method,
  );

  const data = assertBoardingOk(response, method);

  return {
    data,
    boarding: data.boarding ? mapBoardingDetail(data.boarding) : null,
  };
}

/**
 * One pet goes home; the booking runs on.
 *
 * NOT check-out: it touches no `record_status` and no invoice, and `room_released` is always
 * false — the room stays occupied because the other animals are still in it. Mapping this
 * response through the booking-level check-out mapping would show an empty kennel with pets in it.
 */
export async function DepartBoardingOccupant(
  loading: Ref<boolean>,
  payload: { boardingId: string; pet: string; note?: string | null },
): Promise<{ remainingActiveOccupants: number | null; boarding: BoardingDetail | null }> {
  return withFrappeLoading(loading, async () => {
    const { data, boarding } = await postOccupantAction(BOARDING_METHODS.departOccupant, {
      boarding: payload.boardingId,
      pet: payload.pet,
      note: payload.note ?? null,
      // `departed_at` is deliberately NOT sent: custody timestamps are the server's, and a
      // client clock competes with the one the nights are billed against.
    });

    return {
      remainingActiveOccupants: money(data, ["remaining_active_occupants"]),
      boarding,
    };
  });
}

/**
 * Move one pet to another room. No financial effect by design — rates come from the pet's
 * animal type and boarding type, never the room, so every room costs the same.
 */
/**
 * Pets the call declined to move WHILE STILL RETURNING SUCCESS.
 *
 * ⚠️ **`skipped` is not in the contract** — §3.3 documents `success`, `boarding_id`, `pet`,
 * `from_room`, `to_room`, `booking_room`, `total_cost` and `boarding`, and no skip key at all. It
 * is read tolerantly for exactly that reason: a count, a list of ids, a list of objects and a bare
 * flag all normalise here, because the one outcome that must never happen again is a skip being
 * counted as a move. A transfer of three moved two and reported success precisely because nothing
 * read this.
 *
 * Returns a COUNT as well as names: if the backend sends a bare number there are no names to give,
 * and "1 was not moved" is still infinitely better than silence.
 */
function readSkippedPets(data: any): { count: number; pets: string[] } {
  const raw = data?.skipped ?? data?.skipped_pets ?? data?.skippedPets ?? null;

  if (raw === null || raw === undefined || raw === false)
    return { count: 0, pets: [] };

  if (raw === true)
    return { count: 1, pets: [] };

  if (typeof raw === "number")
    return { count: Number.isFinite(raw) ? Math.max(0, Math.trunc(raw)) : 0, pets: [] };

  if (Array.isArray(raw)) {
    const pets = raw
      .map(entry => typeof entry === "string"
        ? entry.trim()
        : String(entry?.pet ?? entry?.pet_id ?? entry?.name ?? "").trim())
      .filter(Boolean);

    // `raw.length`, not `pets.length`: an entry whose id could not be read was still a skip.
    return { count: raw.length, pets };
  }

  // A truthy scalar or object in a shape not seen here. Something was skipped; say so.
  const single = String(raw?.pet ?? raw?.pet_id ?? raw?.name ?? raw ?? "").trim();

  return { count: 1, pets: single ? [single] : [] };
}

export async function TransferBoardingOccupantRoom(
  loading: Ref<boolean>,
  payload: { boardingId: string; pet: string; serviceRoom: string; note: string },
): Promise<{
  toRoom: string | null;
  bookingRoom: string | null;
  totalCost: number | null;
  boarding: BoardingDetail | null;
  skippedCount: number;
  skippedPets: string[];
}> {
  return withFrappeLoading(loading, async () => {
    const { data, boarding } = await postOccupantAction(BOARDING_METHODS.transferOccupantRoom, {
      boarding: payload.boardingId,
      pet: payload.pet,
      service_room: payload.serviceRoom,
      // THE REASON. Without it the backend defaults to "Transferred from <old room>." and the
      // stint row — the only record of why an animal moved — says nothing.
      note: payload.note,
    });

    const skipped = readSkippedPets(data);

    return {
      toRoom: text(data, ["to_room"]),
      // ⚠️ NOT `to_room`. The booking's own room only moves when every remaining active occupant
      // is in the same place; during a split it stays where the rest are.
      bookingRoom: text(data, ["booking_room"]),
      // Returned so the client can assert it did not change.
      totalCost: money(data, ["total_cost"]),
      boarding,
      // ⚠️ A 200 IS NOT A MOVE. Callers must check this before counting a success — see
      // `readSkippedPets`. Non-zero has to reach the operator.
      skippedCount: skipped.count,
      skippedPets: skipped.pets,
    };
  });
}

/** A pet joins an existing booking, paying from its own arrival rather than the booking's start. */
export async function AddBoardingOccupant(
  loading: Ref<boolean>,
  payload: {
    boardingId: string;
    pet: string;
    boardingType?: string | null;
    serviceRoom?: string | null;
    note?: string | null;
  },
): Promise<{ activeOccupantCount: number | null; boarding: BoardingDetail | null }> {
  return withFrappeLoading(loading, async () => {
    const { data, boarding } = await postOccupantAction(BOARDING_METHODS.addOccupant, {
      boarding: payload.boardingId,
      pet: payload.pet,
      // Omitted → the booking's own values. Per-occupant boarding type is legitimate: a pet may
      // join a Travel booking on Treatment terms.
      boarding_type: payload.boardingType ?? null,
      service_room: payload.serviceRoom ?? null,
      note: payload.note ?? null,
    });

    return {
      activeOccupantCount: money(data, ["active_occupant_count"]),
      boarding,
    };
  });
}

/**
 * Money off a method response. `null` (not 0) when the key is absent or unparseable — 0 is a
 * real amount ("no deposit was allocated") and must not be manufactured from a missing field.
 */
function money(raw: any, keys: string[]): number | null {
  for (const key of keys) {
    const value = raw?.[key];
    if (value === null || value === undefined || value === "")
      continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed))
      return parsed;
  }

  return null;
}

function text(raw: any, keys: string[]): string | null {
  for (const key of keys) {
    const value = raw?.[key];
    if (value !== null && value !== undefined && String(value).trim())
      return String(value).trim();
  }

  return null;
}

export async function CheckInPetBoarding(
  loading: Ref<boolean>,
  payload: BoardingCheckInPayload,
): Promise<BoardingCheckInResult | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return mockCheckInBoarding(payload);

    // Errors propagate (no swallow) so the caller can surface the backend's real message.
    //
    // `check_in` IS sent, and only when the desk actually backdated it. Still no `check_out`:
    // the departure timestamp remains the server's, and the server still prices the stay from
    // its own `stay_days` — this states when custody BEGAN, it does not compute anything.
    // Omitted for an ordinary same-day admission, so that path is byte-identical to before.
    //
    // ONE deposit key. `advance_amount` used to be sent alongside as a hedge from when the
    // backend key was unsettled; `check_in_boarding(boarding_id, deposit=None)` does not accept
    // it and Frappe filters unknown kwargs out of `form_dict`, so it was silently discarded.
    //
    // TWO note keys, and unlike the deposit above that is deliberate. `check_in_note` is the
    // Pet Boarding FIELDNAME the value has to land in; `notes` is the kwarg this endpoint has
    // always been called with. Which of the two `check_in_boarding` actually declares is not
    // confirmed, and dropping the wrong one stops check-in notes saving at all — so both go,
    // carrying the same value. Frappe filters the unused one out of `form_dict`. Collapse to
    // one the moment the signature is verified.
    const requestedCheckIn = dateOnly(payload.checkIn);

    const response = await boardingService.post<any, {
      boarding_id: string;
      check_in?: string | null;
      deposit?: number | null;
      notes?: string | null;
      check_in_note?: string | null;
      billable_items?: BoardingBillableItemWrite[];
    }>(
      {
        boarding_id: payload.boardingId,
        // `check_in` is the Pet Boarding FIELDNAME, and the same key the read side already
        // maps first (`healthcareApi.utils.ts`). Sent ONLY when backdated: an absent key lets
        // the server stamp "now" exactly as it always has.
        ...(requestedCheckIn ? { check_in: requestedCheckIn } : {}),
        deposit: payload.deposit,
        notes: payload.notes,
        check_in_note: payload.notes,
        billable_items: payload.billableItems,
      },
      BOARDING_METHODS.checkIn,
      {},
      undefined,
      FrappeMethodType.method,
    );

    // The response is REPORTED BACK, not discarded: a deposit raises a submitted Payment Entry
    // and the desk has to see the amount while the guardian is still at the counter.
    //
    // Asserted, not assumed. `normalizeMethodPayload` alone reads an `ok:false` refusal as an
    // empty payload, and the `?? payload.boardingId` fallback below then rebuilt a plausible
    // result out of what we had SENT - a green "Checked in" for a refused admission.
    const raw = assertBoardingOk(response, BOARDING_METHODS.checkIn);
    const boardingId = text(raw, ["boarding_id", "name"]) ?? String(payload.boardingId);

    return {
      boardingId,
      deposit: money(raw, ["deposit", "deposit_amount"]) ?? payload.deposit ?? null,
      depositPaymentEntry: text(raw, ["deposit_payment_entry", "payment_entry"]),
      checkInApplied: await resolveAppliedCheckIn(raw, boardingId, requestedCheckIn),
    };
  });
}

/** The date half of a `YYYY-MM-DD` or `YYYY-MM-DD HH:MM:SS` value; `null` for anything else. */
function dateOnly(value: unknown): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value ?? "").trim());

  return match ? match[1] : null;
}

/**
 * What check-in date the record ACTUALLY ends up carrying.
 *
 * ⚠️ THE FAILURE THIS GUARDS IS SILENT BY CONSTRUCTION. Frappe filters unknown kwargs out of
 * `form_dict`, so a backend that does not (yet) declare `check_in` discards a backdated date
 * with no error, no warning and a perfectly green response — the desk reads the past date it
 * typed while the stay bills from today. Whether `check_in_boarding` accepts the key is
 * currently UNVERIFIED (see `docs/README.boarding-backdated-check-in-backend-prompt.md`), and
 * an unverified write to a field the stay is BILLED on has to be checked rather than assumed.
 *
 * Costs nothing on the ordinary path: no backdating requested → no work. Costs one extra read
 * only when the response does not already echo the date back, which is exactly the case where
 * we cannot otherwise tell.
 *
 * Returns `null` for UNVERIFIED — never a guess. A read that fails must not raise a false alarm
 * about a date the server may well have stored correctly.
 */
async function resolveAppliedCheckIn(
  raw: any,
  boardingId: string,
  requestedCheckIn: string | null,
): Promise<string | null> {
  if (!requestedCheckIn)
    return null;

  const echoed = dateOnly(text(raw, ["check_in", "check_in_date"]));
  if (echoed)
    return echoed;

  const detail = await GetPetBoardingDetail(ref(false), { boardingId }).catch(() => null);

  return dateOnly(detail?.checkIn);
}

export async function CheckOutPetBoarding(
  loading: Ref<boolean>,
  payload: BoardingCheckOutPayload,
): Promise<BoardingCheckOutResult | null> {
  return withFrappeLoading(loading, async () => {
    if (payload.discount) {
      if (!validBoardingDiscount(payload.discount.type, payload.discount.value))
        throw new Error("BOARDING_DISCOUNT_INVALID");
      const detail = HEALTHCARE_BOARDING_USE_MOCKS ? null
        : await GetPetBoardingDetail(ref(false), { boardingId: payload.boardingId });
      if (!detail?.boardingDiscountSupported)
        throw new Error("BOARDING_DISCOUNT_UNAVAILABLE");
    }

    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return mockCheckOutBoarding(payload);

    // Do NOT swallow the error — let the ApiError (carrying the backend's real message, e.g.
    // the zero-total invoice failure) propagate so the caller can surface it, like the visit
    // pages do. Swallowing it here hid the actual cause behind a generic toast.
    // No `check_out`: the backend stamps the actual departure time (see check-in above).
    // `check_out_note` is the Pet Boarding FIELDNAME and is the one that has to stick; the two
    // `checkout_*` spellings this call already sent are kept for the same reason as check-in —
    // the endpoint's declared kwarg is unconfirmed and all three carry the same value.
    const response = await boardingService.post<any, {
      boarding_id: string;
      discount?: BoardingCheckOutPayload["discount"];
      check_out_note?: string | null;
      checkout_note?: string | null;
      checkout_notes?: string | null;
      billable_items?: BoardingBillableItemWrite[];
    }>(
      {
        boarding_id: payload.boardingId,
        ...(payload.discount && payload.discount.value > 0 ? { discount: payload.discount } : {}),
        check_out_note: payload.checkoutNote,
        checkout_note: payload.checkoutNote,
        checkout_notes: payload.checkoutNote,
        billable_items: payload.billableItems,
      },
      BOARDING_METHODS.checkOut,
      {},
      undefined,
      FrappeMethodType.method,
    );

    // These figures exist ONLY here. `invoice_grand_total` is the invoice's, not this stay's,
    // and the unallocated remainder is the guardian's credit — both are lost forever if the
    // response is dropped on the floor, which is what this function used to do.
    const raw = assertBoardingOk(response, BOARDING_METHODS.checkOut);
    const accommodation = readBoardingAccommodationTotals(raw);

    return {
      boardingId: text(raw, ["boarding_id", "name"]) ?? String(payload.boardingId),
      ...accommodation,
      discountVerificationFailed: Boolean(payload.discount && payload.discount.value > 0 && (
        accommodation.discount?.type !== payload.discount.type
        || accommodation.discount?.value !== payload.discount.value
        || accommodation.accommodationSubtotal == null
        || accommodation.accommodationDiscountAmount == null
        || accommodation.accommodationTotal == null
      )),
      invoiceGrandTotal: money(raw, ["invoice_grand_total", "grand_total"]),
      deposit: money(raw, ["deposit", "deposit_amount"]),
      depositAllocated: money(raw, ["deposit_allocated"]),
      depositUnallocatedRemainder: money(raw, ["deposit_unallocated_remainder"]),
      depositPaymentEntry: text(raw, ["deposit_payment_entry", "payment_entry"]),
      // The invoice is now POSTED at check-out, not left as a draft. `undefined` maps to
      // `null` and not to `false`: an older backend that omits the key must read as
      // "unverified", because a posted invoice mistaken for a draft gets routed to the
      // till, where it is refused, and a draft mistaken for posted gets collected twice.
      invoiceSubmitted: raw?.invoice_submitted === undefined || raw?.invoice_submitted === null
        ? null
        : Boolean(raw.invoice_submitted),
      invoiceOutstanding: money(raw, ["invoice_outstanding"]),
      totalPaid: money(raw, ["total_paid"]),
      salesInvoice: text(raw, ["sales_invoice", "invoice"]),
      payments: readBoardingPayments(raw),
    };
  });
}

/**
 * Record a payment against a stay — a deposit top-up, an instalment, or the final settlement.
 *
 * ⚠️ THIS IS THE FIX FOR "A GUARDIAN COULD ONLY PAY ONCE". The old deposit path returned the
 * EXISTING Payment Entry on a second call, so the money silently vanished from the stay and
 * desks worked around it with hand-made Payment Entries that carried no link to the booking.
 * Check-out never saw those, the stay was invoiced in full, and the till asked for the whole
 * amount a second time. A NEW `paymentEntry` id coming back is the signal that this worked.
 *
 * ⚠️ NO MODE ARGUMENT, AND THAT IS THE CONTRACT, NOT AN OVERSIGHT HERE. The backend decides
 * where the money goes: before check-out there is no invoice so it is held as an advance on
 * the customer; after check-out it is applied straight to the posted invoice. The client must
 * not try to choose, and must not assume which happened — read `salesInvoice` to find out.
 *
 * Only `boarding_id` is sent. The double-spelling hedge used by check-in/check-out was for
 * kwargs whose names were unconfirmed; this one is documented, and sending both risks binding
 * a signature that accepts either.
 */
export async function RecordBoardingPayment(
  loading: Ref<boolean>,
  payload: BoardingPaymentPayload,
): Promise<BoardingPaymentResult> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return mockRecordBoardingPayment(payload);

    // Errors PROPAGATE. Every refusal this endpoint raises names a figure or a state the desk
    // must act on — "only 75,000 is outstanding", "already settled in full" — and the dialog
    // holds it on screen beside the amount field rather than toasting it away.
    const { data, boarding } = await postOccupantAction(BOARDING_METHODS.recordPayment, {
      boarding_id: payload.boardingId,
      amount: payload.amount,
      note: payload.note ?? null,
    });

    return {
      boardingId: text(data, ["boarding_id", "boarding", "name"]) ?? String(payload.boardingId),
      paymentEntry: text(data, ["payment_entry", "name"]),
      amount: money(data, ["amount", "paid_amount"]),
      account: text(data, ["account", "paid_to"]),
      // Read with `text`, so an empty string collapses to null exactly like a missing key —
      // both mean the same thing here, and the UI keys its "unattributed" warning off null.
      posProfile: text(data, ["pos_profile"]),
      totalPaid: money(data, ["total_paid"]),
      totalCost: money(data, ["total_cost"]),
      balance: money(data, ["balance"]),
      salesInvoice: text(data, ["sales_invoice", "invoice"]),
      invoiceOutstanding: money(data, ["invoice_outstanding"]),
      payments: readBoardingPayments(data),
      boarding,
    };
  });
}

/**
 * Cancel a boarding — Pending Room or Reserved only (backend enforces; a Checked-In pet is in
 * custody and must go through check-out instead). `note` is REQUIRED, like start_visit_boarding
 * and referral: a custody change always carries a written reason. Errors PROPAGATE (no swallow)
 * so the caller surfaces the backend's real rejection. On success the backend clears the visit's
 * `boarding` link and unblocks completion — the caller must REFETCH; we never write status here.
 */
export async function CancelPetBoarding(
  loading: Ref<boolean>,
  payload: BoardingCancelPayload,
): Promise<boolean> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return true;

    const response = await boardingService.post<any, {
      boarding: string;
      note: string;
    }>(
      {
        boarding: payload.boardingId,
        note: payload.note,
      },
      BOARDING_METHODS.cancel,
      {},
      undefined,
      FrappeMethodType.method,
    );

    // `return true` unconditionally was a lie whenever the refusal arrived as 200 `ok:false` -
    // "Checked-in boarding must be checked out, not cancelled" became "Boarding cancelled".
    assertBoardingOk(response, BOARDING_METHODS.cancel);

    return true;
  });
}

export async function SyncPetBoardingBillableItems(
  loading: Ref<boolean>,
  payload: BoardingBillableItemsPayload,
): Promise<boolean> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return mockSyncBoardingBillableItems(payload);

    // Errors propagate (no swallow) so the caller can surface the backend's real message.
    const response = await boardingService.post<any, {
      boarding_id: string;
      billable_items: BoardingBillableItemWrite[];
    }>(
      {
        boarding_id: payload.boardingId,
        billable_items: payload.billableItems,
      },
      BOARDING_METHODS.syncBillableItems,
      {},
      undefined,
      FrappeMethodType.method,
    );

    // A refused save must not report "Saved" - the operator would leave believing the lines
    // were priced and stored.
    assertBoardingOk(response, BOARDING_METHODS.syncBillableItems);

    return true;
  });
}

export interface StartVisitBoardingPayload {
  /** Vet Visit name. Backend derives pet/guardian/episode + the initiating user from it. */
  visit: string;
  /** Required boarding reason. Whitespace-only is rejected server-side. */
  note: string;
  /** Optional planned checkout date (Y-m-d). */
  expected_check_out?: string | null;
}

/**
 * Start boarding from a visit — creates a "Pending Room" Pet Boarding for the visit's pet,
 * for the desk to assign a room to. Backend owns the transition and validation; it does NOT
 * change the visit status. Errors PROPAGATE so the caller surfaces the backend's specific
 * rejection (e.g. "Completed visits cannot be referred"). See docs/VISIT_BOARDING_CONTRACT.md.
 */
export async function StartVisitBoarding(loading: Ref<boolean>, payload: StartVisitBoardingPayload): Promise<void> {
  await withFrappeLoading(loading, async () => {
    const response = await boardingService.post<any, {
      visit: string;
      note: string;
      expected_check_out?: string | null;
    }>(
      {
        visit: payload.visit,
        note: payload.note,
        expected_check_out: payload.expected_check_out ?? null,
      },
      BOARDING_METHODS.startVisitBoarding,
      {},
      undefined,
      FrappeMethodType.method,
    );

    assertBoardingOk(response, BOARDING_METHODS.startVisitBoarding);
  });
}

/**
 * Create a clinical order linked directly to a boarding record (no visit).
 * `kind` is one of lab / radiology / service / medication / procedure — see
 * docs/README.boarding-create-order-backend-prompt.md. Backend:
 * `pet_app.api.healthcare.boarding.create_order`.
 */
export async function CreateBoardingOrder(
  loading: Ref<boolean>,
  payload: BoardingOrderPayload,
): Promise<BoardingOrderResult> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS) {
      return {
        kind: payload.kind,
        orderId: `MOCK-ORD-${payload.kind}`,
        itemId: `MOCK-ITEM-${payload.kind}`,
        reused: false,
      };
    }

    // Errors propagate (no swallow) so the caller can surface the backend's real message.
    const response = await boardingService.post<any, {
      boarding_id: string;
      pet: string;
      kind: string;
      template_id: string;
      item_code?: string | null;
      care_service_id?: string | null;
      priority?: string | null;
      note?: string | null;
      dose_option?: string | null;
      warehouse?: string | null;
      dosage?: string | null;
      frequency?: string | null;
      duration_days?: string | null;
      scheduled_datetime?: string | null;
    }>(
      {
        boarding_id: payload.boardingId,
        // REQUIRED. Absent → "Pet is required. Send the pet this order is for."
        pet: payload.petId,
        kind: payload.kind,
        template_id: payload.templateId,
        item_code: payload.itemCode ?? null,
        care_service_id: payload.careServiceId ?? null,
        priority: payload.priority ?? null,
        note: payload.note ?? null,
        // Medication only. Stock-per-dose lives on the dose option, so a dispense cannot resolve
        // a quantity without it. Both are declared parameters — docs/BOARDING_CREATE_ORDER_API.md
        // §2 / §2.1, whose table is verified against the live signature — and are stamped on the
        // billable row at order time, then read at dispense time.
        //
        // ⚠️ Do NOT add keys here speculatively. This endpoint takes no `**kwargs`, so Frappe
        // filters any name outside the contract's §2 table out of `form_dict` before the call:
        // no error, no effect. The signature is **14 parameters** — the five below plus
        // `boarding_id`, `kind`, `pet`, `template_id`, `care_service_id`, `item_code`,
        // `priority`, `note`, and the three medication schedule fields.
        //
        // ⚠️ COUNT THE CONTRACT'S TABLE, NOT ITS PROSE. That doc's sentence has been wrong twice
        // — it claimed nine when there were 12, and 12 when there were 13. The table is the
        // verified signature; the sentence is a hand-maintained tally that keeps falling behind.
        dose_option: payload.doseOption ?? null,
        warehouse: payload.warehouse ?? null,
        // The schedule. Until these landed a boarding medication recorded WHAT and HOW MUCH PER
        // DOSE but not how often or for how long, so a five-day twice-daily course and a single
        // dose were stored identically and were indistinguishable after the fact.
        //
        // ⚠️ `duration_days` is sent as a **STRING**. The backend field is `Data`, not `Int`;
        // Frappe coerces a null `Int` to `0`, and "0 days" is a false clinical statement where
        // blank is honest. Same shape as the visit's field. Nothing multiplies `qty` or `amount`
        // by it — verified backend-side in three places — so this is clinical grain only and
        // carries no billing effect.
        //
        // All three are optional and their ABSENCE must stay valid: a single one-off dose during
        // a stay is a real case and must not be forced to invent a frequency. Null, never "".
        dosage: payload.dosage ?? null,
        frequency: payload.frequency ?? null,
        duration_days: payload.durationDays ?? null,
        // WHEN the order happens — `Y-m-d H:i:S`, all four clinical kinds.
        //
        // ⚠️ Null, never a fallback to "now". The backend deliberately does not default this
        // either, which is the only reason an unscheduled order stays distinguishable from one
        // booked for this moment. Manufacturing a timestamp here would destroy that on the way
        // out and there would be no way to get it back.
        //
        // `||`, not `??`: clearing the picker yields "", and an empty string is a VALUE where
        // absence is the fact. Same discipline as `dosage` / `frequency` on the medication path.
        scheduled_datetime: payload.scheduledDatetime || null,
      },
      BOARDING_METHODS.createOrder,
      {},
      undefined,
      FrappeMethodType.method,
    );

    /**
     * Envelope discipline, matching `transition_order` in orderApi.ts.
     *
     * A bare `normalizeMethodPayload` is deliberately NOT used here: it unwraps straight to
     * `data` and would throw `ok` and `errors` away, which are the only things that distinguish
     * a rejection from a success. Failure is matched on `ok: false` and NOTHING ELSE —
     * `meta.code` is not stable across error sources, so switching on it silently
     * misclassifies whichever source it wasn't written against.
     *
     * Read from `data`. Every key in it is ALSO mirrored at the top level of the envelope for
     * legacy callers (contract §3); `data` is the authoritative copy and the mirrors may lag.
     */
    const data = assertBoardingOk(response, BOARDING_METHODS.createOrder);

    return {
      kind: payload.kind,
      orderId: text(data, ["order_id"]),
      itemId: text(data, ["item_id"]),
      // Boolean(), not `=== true` — 1/0 from the backend still reads correctly.
      reused: Boolean(data.reused),
    };
  });
}

/**
 * The handle a caller can actually FETCH.
 *
 * For medication there is no order document, so `order_id` is opaque and only the billable
 * row (`item_id`) exists. Reaching for `orderId` directly is the bug this exists to prevent.
 */
export function boardingOrderFetchableId(result: BoardingOrderResult): string | null {
  return result.kind === "medication" ? result.itemId : result.orderId;
}

/**
 * Record a scheduled medication dose as administered during a stay. The backend advances/completes
 * the target `Pet Care Plan Item` (medication/injection) WITHOUT closing the case or episode — the
 * clinical visit stays the source of truth. Gated by `permissions.can_give_medication`; callers must
 * still check that flag before showing the action. Backend:
 * `pet_app.api.healthcare.boarding.record_medication_given` — see
 * docs/README.boarding-medication-give-backend-prompt.md.
 */
export async function RecordBoardingMedicationGiven(
  loading: Ref<boolean>,
  payload: BoardingMedicationGivenPayload,
): Promise<boolean> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return true;

    // Errors propagate so the caller surfaces the backend's real message.
    const response = await boardingService.post<any, {
      boarding: string;
      plan_item: string;
      note?: string | null;
      given_at?: string | null;
    }>(
      {
        boarding: payload.boardingId,
        plan_item: payload.planItem,
        note: payload.note ?? null,
        given_at: payload.givenAt ?? null,
      },
      BOARDING_METHODS.recordMedicationGiven,
      {},
      undefined,
      FrappeMethodType.method,
    );

    // A dose recorded as given when the backend refused is a clinical record that is WRONG.
    assertBoardingOk(response, BOARDING_METHODS.recordMedicationGiven);

    return true;
  });
}

/**
 * Dispense a requested medication line on a boarding stay (a `billable_items` row created via
 * `create_order` kind="medication"). Marks the line dispensed — inventory is owned by the backend
 * (deducted on the boarding Sales Invoice at checkout), so no stock math here. Gated by
 * `permissions.can_dispense_medication`. Backend:
 * `pet_app.api.healthcare.boarding.dispense_medication` — see
 * docs/README.boarding-medication-give-backend-prompt.md.
 */
export async function DispenseBoardingMedication(
  loading: Ref<boolean>,
  payload: BoardingMedicationDispensePayload,
): Promise<boolean> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_BOARDING_USE_MOCKS)
      return true;

    const response = await boardingService.post<any, {
      boarding: string;
      item_id: string;
      qty?: number | null;
      note?: string | null;
    }>(
      {
        boarding: payload.boardingId,
        item_id: payload.itemId,
        qty: payload.qty ?? null,
        note: payload.note ?? null,
      },
      BOARDING_METHODS.dispenseMedication,
      {},
      undefined,
      FrappeMethodType.method,
    );

    // Stock moves on a dispense. Reporting one that did not happen desynchronises the warehouse.
    assertBoardingOk(response, BOARDING_METHODS.dispenseMedication);

    return true;
  });
}
